import {DestroyRef, Inject, Injectable, DOCUMENT} from '@angular/core';
import {MetaData, NgEventBus} from 'ng-event-bus';
import {LookupService} from '../../common/service/lookup.service';
import {MapToolsService} from './map-tools.service';
import {MapEventsSelectionService} from './map-events-selection.service';
import {AppConfigService} from '../../common/service/app-config.service';
import {AngleUtilService} from './features/angle/angle-util.service';
import {SelectionUtilityService} from './selection-utility.service';
import {GraphicSelectionService} from './graphic-selection.service';
import {PlanModelService} from '../../common/service/plan-model.service';
import {SpatialUtilService} from './spatial-util.service';
import {MapModeService} from './map-mode.service';
import {WidgetService} from './widget.service';
import {LayerUtilService} from '../layers/layer-util.service';
import {EsriModuleService} from '../esri-nlr/services/esri-module.service';

import {PermissionsService} from '../../common/service/permissions.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
@Injectable({
    providedIn: 'root'
})
export class MapEventsService {
    private oldScale: number;
    mapServiceLayer: any;
    private pointTypeLookups: any;
    constructor(
        private eventBus: NgEventBus,
        private lookupService: LookupService,
        private mapToolsService: MapToolsService,
        private mapEventsSelectionService: MapEventsSelectionService,
        private appConfigService: AppConfigService,
        private angleUtilService: AngleUtilService,
        private selectionUtilityService: SelectionUtilityService,
        private graphicSelectionService: GraphicSelectionService,
        private permissionService: PermissionsService,
        private planModelService: PlanModelService,
        private spatialUtilService: SpatialUtilService,
        private mapModeService: MapModeService,
        private widgetService: WidgetService,
        private layerUtilService: LayerUtilService,
        private esriModuleService: EsriModuleService,
        private destroyRef: DestroyRef,
        @Inject(DOCUMENT) private document: Document
    ) {
        this.eventBus
            .on('filterFeatureSelection')
            .pipe(takeUntilDestroyed(destroyRef))
            .subscribe((meta: MetaData<boolean>) => {
                console.debug(this.title + ' : filterFeatureSelection by watch');
                this.enablePolygonPolylineSelection = meta.data;
            });

        this.eventBus
            .on('updateGraphicToModify')
            .pipe(takeUntilDestroyed(destroyRef))
            .subscribe((meta: MetaData) => {
                console.debug(this.title + ' : updateGraphicToModify by watch');
                mapEventsSelectionService.graphicToModify = new this.esriModuleService.Graphic(meta.data.toJson());
            });

        this.eventBus
            .on('reApplySelection')
            .pipe(takeUntilDestroyed(destroyRef))
            .subscribe((meta: MetaData) => {
                console.debug(this.title + ' : reApplySelection by watch');
                this.updateEnlargementSelection(meta.data, meta.data);
            });

        this.eventBus
            .on('mapRefresh')
            .pipe(takeUntilDestroyed(destroyRef))
            .subscribe(() => {
                console.debug(this.title + ' : map refresh by watch');

                if (mapToolsService.isScaleActive()) {
                    return;
                }

                const scale = parseInt(this.currentMap.getScale());
                this.oldScale = scale;

                this.currentMap.setScale(scale - 1).then(() => {
                    this.currentMap.setScale(this.oldScale).then(() => {
                        console.info('finished map refresh');
                    });
                });
            });
    }
    title = 'MapEventsService';
    currentMap = undefined;
    enablePolygonPolylineSelection = false;
    mapClickEvent = undefined;
    graphicBeforeMove = undefined;

    getDefaultBlackCursor = () =>
        "url('" + this.appConfigService.getDesignProperties().DESIGN_CURSORS_CONFIGURATION.CURSOR_SELECT_BLACK + "') 13 13, auto";

    enlargementDrawToolbar = undefined;
    enlargementEditToolbar = undefined;
    drawToolbarActivated = false;

    setupNavToolbar(map, callback) {
        console.debug(this.title + ' : setupNavToolbar');
        const navToolbar = new this.esriModuleService.Navigation(map);

        navToolbar.on('extent-history-change', () => {
            console.debug(this.title + ' : setupNavToolbar - extent-history-change');

            if (navToolbar.activated) {
                this.eventBus.cast('ZoomInToExtentFinished');
            }
        });

        callback(navToolbar);
    }

    updateEnlargementSelection(newGraphic, oldGraphic) {
        this.eventBus.cast('update-enlargement-selection', {
            newGraphic: newGraphic,
            oldGraphic: oldGraphic,
            graphicBeforeMove: this.graphicBeforeMove
        });
    }

    setupMapEvents(map) {
        this.currentMap = map;

        map.on('extent-change', (event: any) => {
            console.debug(this.title + ' : setupMapEvents - extent-change');

            const sc = map.getScale();

            if (sc && !isNaN(sc)) {
                this.eventBus.cast('scaleMapEvent', sc);
            }
        });
        if (map.loaded) {
            this.mapEventsSetup(map);
        } else {
            map.on('load', (event: any) => {
                this.mapEventsSetup(map);
            });
        }
    }

    private mapEventsSetup(map) {
        console.debug(this.title + ' : setupMapEvents - load');

        map.disablePan();
        map.setMapCursor(this.getDefaultBlackCursor());
        map.optimizePanAnimation = false;

        const symbol = new this.esriModuleService.SimpleFillSymbol(
            this.esriModuleService.SimpleFillSymbol.STYLE_SOLID,
            this.esriModuleService.SimpleLineSymbol(
                this.esriModuleService.SimpleLineSymbol.STYLE_SOLID,
                this.esriModuleService.Color([0, 112, 255]),
                4
            ),
            new this.esriModuleService.Color([178, 178, 178, 0.25]),
            1
        );

        this.enlargementDrawToolbar = this.esriModuleService.Draw(map);
        this.enlargementDrawToolbar.setFillSymbol(symbol);

        this.enlargementDrawToolbar.on('draw-end', evt => {
            const uniquePoints = evt.geometry.rings[0].filter((v, i, a) => i === a.findIndex(p => p[0] === v[0] && p[1] === v[1]));
            if (uniquePoints.length <= 2) {
                return;
            }

            console.debug(this.title + ' : setupMapEvents - draw-end');

            this.enlargementDrawToolbar.deactivate();

            const graphic = new this.esriModuleService.Graphic(evt.geometry, symbol);

            this.eventBus.cast('enlargement-draw-end', {
                flag: true,
                graphic: graphic,
                mapMode: this.mapModeService.mapMode
            });

            this.drawToolbarActivated = false;
        });
        this.enlargementEditToolbar = new this.esriModuleService.Edit(map);

        this.enlargementEditToolbar.on('graphic-move-stop', e => {
            this.selectionUtilityService.clearGraphics(map);
            if (e.graphic.attributes['type'] === 'enlargementGraphic') {
                this.updateEnlargementSelection(e.graphic, e.graphic);
            }

            if (e.graphic.attributes['type'] === 'clippingGraphic') {
                const polygonGeometry = new this.esriModuleService.Polygon();
                polygonGeometry.setSpatialReference(e.graphic.geometry.spatialReference);
                polygonGeometry.addRing(e.graphic.geometry.rings[1]);
                const graphic = new this.esriModuleService.Graphic(this.spatialUtilService.copyGeometry(polygonGeometry));
                this.eventBus.cast('enlargement-graphic-move-stop', {
                    graphic: graphic,
                    geometry: e.graphic.geometry
                });
            }
        });

        this.enlargementEditToolbar.on('graphic-first-move', e => {
            this.graphicBeforeMove = new this.esriModuleService.Graphic(this.spatialUtilService.copyGeometry(e.graphic.geometry));
        });

        this.enlargementEditToolbar.on('vertex-move-stop', e => {
            this.graphicBeforeMove = new this.esriModuleService.Graphic(this.spatialUtilService.copyGeometry(e.graphic.geometry));
            const polygonGeometry = new this.esriModuleService.Polygon();
            polygonGeometry.setSpatialReference(e.graphic.geometry.spatialReference);
            polygonGeometry.addRing(e.graphic.geometry.rings[1]);
            const graphic = new this.esriModuleService.Graphic(this.spatialUtilService.copyGeometry(polygonGeometry));
            this.eventBus.cast('enlargement-vertex-move-stop', {
                graphic: graphic,
                geometry: e.graphic.geometry
            });
        });

        //Activate the toolbar when you click on a graphic
        map.graphics.on('click', evt => {
            if (!this.permissionService.hasEditPermission(['EDIT_DESIGN'])) {
                return;
            }

            if (!this.drawToolbarActivated && this.mapToolsService.isActive('ENLARGEMENT_WINDOW')) {
                const markerSymbol = new this.esriModuleService.SimpleMarkerSymbol();
                markerSymbol.setColor(new this.esriModuleService.Color(new this.esriModuleService.Color([255, 0, 0, 1]), 4));
                markerSymbol.setOutline(null);

                const options = {
                    allowAddVertices: true,
                    allowDeleteVertices: true,
                    uniformScaling: true,
                    boxHandleSymbol: markerSymbol
                };

                this.eventBus.cast('active-enlargement-window-click', {graphic: evt.graphic, options: options});
            }
        });

        this.pointTypeLookups = this.mapEventsSelectionService.getPointTypeLookups();

        window.setTimeout(() => this.spatialUtilService.refreshMapFeatureLayers(), 0);
    }

    setupSelectToolbar(map) {
        console.debug(this.title + ' : setupSelectToolbar');

        this.currentMap = map;

        this.deleteMapClickHandler();
        this.mapClickEvent = map.on('click', (evt: any) => this.onMapClickHandler(evt));

        return this.initSelectToolbar();
    }

    onMapClickHandler(evt) {
        console.debug(this.title + ' : setupMapEvents - click');

        if (
            (this.enlargementEditToolbar._tool > 0 && this.enlargementEditToolbar._graphic.geometry.getExtent().intersects(evt.graphic.mapPoint)) ||
            (this.enlargementDrawToolbar._tool > 0 && this.enlargementDrawToolbar._graphic.geometry.getExtent().intersects(evt.graphic.mapPoint))
        ) {
            return;
        }

        if (!this.currentMap.snappingManager) {
            this.eventBus.cast('setup-map-events-click', {
                evt: evt,
                currentMap: this.currentMap,
                enablePolygonPolylineSelection: this.enablePolygonPolylineSelection,
                scale: this.currentMap.getScale()
            });
        }
    }

    deleteMapClickHandler() {
        if (this.mapClickEvent) {
            this.mapClickEvent.remove();
        }
    }

    resetMapClickHandler(map) {
        if (this.mapClickEvent) {
            this.mapClickEvent.remove();
        }
        this.mapClickEvent = map.on('click', (evt: any) => this.onMapClickHandler(evt));
    }

    initSelectToolbar() {
        console.debug(this.title + ' : initSelectToolbar');
        this.graphicSelectionService.activate((evt: any) => this.onDrawCompleteHandler(evt));
    }

    onDrawCompleteHandler(event: any) {
        if (!this.selectionUtilityService.isCtrlKeyDown() && !this.selectionUtilityService.isShiftKeyDown()) {
            this.selectionUtilityService.clearGraphics(this.currentMap);
        }

        let selectGeometry = event.geometry;
        // ND-175: buffer lines for selection
        if (event.geometry.type !== 'polygon') {
            // buffer at same distance used for point selection (see MapEventsSelectionService)
            const bufferDistance = this.currentMap.getScale() > 0 ? this.currentMap.getScale() * 0.0024 : 2.4;

            // project geographic coordinates to planar for buffering
            let wkid = undefined;
            if (event.geometry.spatialReference.wkid === this.appConfigService.getDesignProperties().DESIGN_SETTINGS.MAP_SPATIAL_REFERENCE_GDA94) {
                wkid = this.appConfigService.getDesignProperties().DESIGN_SETTINGS.MAP_PROJECTED_SPATIAL_REFERENCE_GDA94;
            } else if (
                event.geometry.spatialReference.wkid === this.appConfigService.getDesignProperties().DESIGN_SETTINGS.MAP_SPATIAL_REFERENCE_GDA2020
            ) {
                wkid = this.appConfigService.getDesignProperties().DESIGN_SETTINGS.MAP_PROJECTED_SPATIAL_REFERENCE_GDA2020;
            }

            if (wkid) {
                const outSpatialReference = new this.esriModuleService.SpatialReference({wkid: wkid});
                const inGeometry = this.esriModuleService.projection.project(event.geometry, outSpatialReference);
                selectGeometry = this.esriModuleService.projection.project(
                    this.esriModuleService.geometryEngine.buffer(inGeometry, bufferDistance, 'meters'),
                    this.esriModuleService.geometry.spatialReference
                );
            } else {
                selectGeometry = this.esriModuleService.geometryEngine.buffer(event.geometry, bufferDistance, 'meters');
            }
        }

        const results = this.graphicSelectionService.featureLayers.map(featureLayer => {
            const features = this.spatialUtilService.getGraphics(featureLayer).filter(f => {
                const spatialQueryFeatureGeometry = this.layerUtilService.getSpatialQueryFeatureGeometry(featureLayer, f);
                if (
                    event.geometry.type === 'polygon' &&
                    this.graphicSelectionService.selectionMethod.name === 'RECTANGLE' &&
                    selectGeometry.rings[0][0][0] > selectGeometry.rings[0][1][0]
                ) {
                    return this.esriModuleService.geometryEngine.contains(selectGeometry.getExtent(), spatialQueryFeatureGeometry);
                }
                return (
                    this.esriModuleService.geometryEngine.intersects(selectGeometry, spatialQueryFeatureGeometry) ||
                    this.esriModuleService.geometryEngine.intersects(selectGeometry, f.geometry)
                );
            });

            const featureLayerName = featureLayer.id.substring(0, featureLayer.id.indexOf('FeatureLayer')).toLowerCase();
            const filteredFeatures = this.graphicSelectionService.applyFeaturesFilter(featureLayerName, features);

            if (filteredFeatures && this.spatialUtilService.someFeaturesHighlighted(featureLayer, filteredFeatures)) {
                this.mapEventsSelectionService.checkAndUnhighlight(this.currentMap, filteredFeatures, featureLayer);
            } else {
                this.spatialUtilService.highlightFeatures(featureLayer, filteredFeatures);
            }

            return {featureLayerName: featureLayerName, features: filteredFeatures || []};
        });

        if (this.mapToolsService.isActive('IDENTIFY_FEATURES')) {
            const allFeatures = results
                .filter(v => v.features.length)
                .reduce((acc, v) => {
                    acc[v.featureLayerName] = v.features;
                    return acc;
                }, {});
            this.eventBus.cast('featuresInteracted', allFeatures);
        }
    }

    applyMovePoint(featureLayer, pointBeforeUpdate, point) {
        console.debug(this.title + ' : applyMovePoint');

        this.makeEditsToPlan(featureLayer, [point], () => {
            this.eventBus.cast('moveFeature', {pointBeforeUpdate: pointBeforeUpdate, point: point, featureLayer: featureLayer});
        });
    }

    rotateAngle(rotationFeature, map) {
        let output = undefined;
        console.debug(this.title + ' : rotateAngle');

        const rotationFeatureCenter = new this.esriModuleService.geometry.Point(rotationFeature.geometry);

        const circle = this.esriModuleService.geometry.Circle({
            center: rotationFeatureCenter,
            //geodesic: true,
            radius: 1.0,
            radiusUnit: 'esriMeters',
            setSpatialReference: map.SpatialReference
        });

        const newLine = [];
        newLine.push(circle.rings[0][0]);
        newLine.push(circle.rings[0][30]);
        circle.rings.push(newLine);

        const sms = new this.esriModuleService.SimpleLineSymbol()
            .setStyle(this.esriModuleService.SimpleLineSymbol.STYLE_SOLID)
            .setColor(new this.esriModuleService.Color([255, 0, 0, 0.75]));
        sms.setWidth(6);

        const calcRotation = this.angleUtilService.calculateRotation(rotationFeature);

        output = this.esriModuleService.Graphic(circle, sms);
        output.geometry = this.esriModuleService.geometryEngine.rotate(output.geometry, calcRotation, rotationFeatureCenter);
        return output;
    }

    clearGraphicToModify = () => {
        console.debug(this.title + ' : clearGraphicToModify');

        this.mapEventsSelectionService.graphicToModify = undefined;
    };

    makeEditsToPlan(featureLayer, array, callback) {
        console.debug(this.title + ' : makeEditsToPlan');
        const featureMap = this.spatialUtilService.getFeatureMap(this.spatialUtilService.getFeatures(featureLayer));
        // Update attribute display values to its real values. This is because the attribute viewer changes the real attribute values
        array.forEach(featureToBeUpdated => {
            const feature = featureMap[featureToBeUpdated.attributes.id];
            Object.keys(featureToBeUpdated.attributes).forEach(attributeName => {
                const attributeValue = featureToBeUpdated.attributes[attributeName];
                if (feature.attributes[attributeName] !== attributeValue) {
                    feature.attributes[attributeName] = attributeValue;
                }
            });

            if (featureToBeUpdated.attributes.x && featureToBeUpdated.attributes.y) {
                feature.attributes.x = featureToBeUpdated.geometry.x;
                feature.attributes.y = featureToBeUpdated.geometry.y;
            }
        });

        this.spatialUtilService.applyEdits(featureLayer, null, array, null).subscribe({
            next: res => {
                console.debug(this.title + ' : makeEditsToPlan - TYPE: ' + featureLayer.name + ' updates: ', res.updates);

                if (featureLayer.id === 'annotationFeatureLayer' || featureLayer.id === 'angleFeatureLayer') {
                    this.currentMap.emit('extent-change', {
                        levelChange: false,
                        lod: null,
                        delta: null,
                        extent: this.currentMap.extent
                    });
                }

                this.spatialUtilService.updateMapGraphicGeometries(featureLayer, array);

                this.eventBus.cast(this.appConfigService.getDesignProperties().EVENTS.POINT_FEATURES_UPDATED, {
                    array: array,
                    featureLayer: featureLayer
                });

                if (callback != null) {
                    callback();
                }
            },
            error: err => {
                console.error(this.title + ' : makeEditsToPlan - ' + featureLayer.name);
            }
        });
    }
}
